import { useState, useRef, useEffect, useLayoutEffect } from 'react';

import { motion } from 'framer-motion';

import { REFERENCE_TYPE_STYLES } from './reference-styles';
import { ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { ChunkDocumentDrawerContent } from '@/new/components/reference-content/chunk-document-drawer-content';
import { DocumentDrawerContent } from '@/new/components/reference-content/document-drawer-content';
import { FactDocumentDrawerContent } from '@/new/components/reference-content/fact-document-drawer-content';
import { ThreadFileChunkDrawerContent } from '@/new/components/reference-content/thread-file-chunk-drawer-content';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

interface ReferenceSidePanelProps {
  onClose: () => void;
  references: { id: string; index: number; referenceType: ReferenceType }[];
  activeReferenceId: string;
  onSelectReference: (id: string, referenceType: ReferenceType) => void;
}

const MIN_WIDTH_PCT = 0.2;
const DEFAULT_WIDTH_PCT = 0.35;
const CONTENT_MIN_WIDTH_PX = 512;

const FALLBACK_CONTAINER_WIDTH = typeof window !== 'undefined' ? window.innerWidth : 1200;
const getWidthFromPct = (containerWidth: number, pct: number) => Math.round(containerWidth * pct);
const getMinPanelWidth = (containerWidth: number) => getWidthFromPct(containerWidth, MIN_WIDTH_PCT);
const getMaxPanelWidth = (containerWidth: number) => containerWidth;
const getInlineMaxPanelWidth = (containerWidth: number) => Math.max(containerWidth - CONTENT_MIN_WIDTH_PX, 0);
const clampPanelWidth = (widthPx: number, containerWidth: number) =>
  Math.min(Math.max(widthPx, getMinPanelWidth(containerWidth)), getMaxPanelWidth(containerWidth));

// Store width outside component to persist across open/close cycles
let lastPanelWidthPx = getWidthFromPct(FALLBACK_CONTAINER_WIDTH, DEFAULT_WIDTH_PCT);

export function ReferenceSidePanel({
  onClose,
  references,
  activeReferenceId,
  onSelectReference,
}: ReferenceSidePanelProps) {
  const [widthPx, setWidthPx] = useState(lastPanelWidthPx);
  const [containerWidth, setContainerWidth] = useState(FALLBACK_CONTAINER_WIDTH);
  const [viewportWidth, setViewportWidth] = useState(FALLBACK_CONTAINER_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [isContainerAdjusting, setIsContainerAdjusting] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const hasMeasuredContainerRef = useRef(false);
  const measuredContainerWidthRef = useRef(FALLBACK_CONTAINER_WIDTH);
  const containerAdjustFrameRef = useRef<number | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useLayoutEffect(() => {
    const container = panelRef.current?.parentElement;
    if (!container) return;

    const syncToContainer = () => {
      const nextContainerWidth = Math.round(container.getBoundingClientRect().width);
      const nextViewportWidth = window.innerWidth;

      if (hasMeasuredContainerRef.current && nextContainerWidth !== measuredContainerWidthRef.current) {
        setIsContainerAdjusting(true);
        if (containerAdjustFrameRef.current) {
          cancelAnimationFrame(containerAdjustFrameRef.current);
        }
        containerAdjustFrameRef.current = requestAnimationFrame(() => {
          setIsContainerAdjusting(false);
          containerAdjustFrameRef.current = null;
        });
      }

      measuredContainerWidthRef.current = nextContainerWidth;
      setContainerWidth(nextContainerWidth);
      setViewportWidth(nextViewportWidth);
      setWidthPx((currentWidth) => {
        const constrainedWidth = clampPanelWidth(currentWidth, nextContainerWidth);
        lastPanelWidthPx = constrainedWidth;
        return constrainedWidth;
      });
      hasMeasuredContainerRef.current = true;
    };

    syncToContainer();

    const resizeObserver = new ResizeObserver(syncToContainer);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (containerAdjustFrameRef.current) {
        cancelAnimationFrame(containerAdjustFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const panelRight = panelRef.current?.parentElement?.getBoundingClientRect().right ?? window.innerWidth;
      const newPx = panelRight - e.clientX;
      const constrainedPx = clampPanelWidth(newPx, containerWidth);
      lastPanelWidthPx = constrainedPx;
      setWidthPx(constrainedPx);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, containerWidth]);

  const activeReference = references.find((ref) => ref.id === activeReferenceId);
  // Once the thread content hits its minimum width, keep the in-flow panel width capped
  // and let the panel continue expanding over the message column like a drawer.
  const reservedWidth = Math.min(widthPx, getInlineMaxPanelWidth(containerWidth));
  const isDrawerMode = widthPx > reservedWidth;
  const drawerExitWidth = getInlineMaxPanelWidth(containerWidth);
  const shouldDisableTransitions = isResizing || isContainerAdjusting;
  const overlayOpacityDuration = isContainerAdjusting ? 0 : 0.2;
  const overlayLeftInset = -Math.max(viewportWidth - reservedWidth, 0);
  const overlayRightInset = isDrawerMode ? 0 : widthPx;

  const handleOverlayClick = () => {
    if (!isDrawerMode) return;
    if (drawerExitWidth <= 0) {
      onClose();
      return;
    }
    lastPanelWidthPx = drawerExitWidth;
    setWidthPx(drawerExitWidth);
  };

  return (
    <motion.div
      ref={panelRef}
      initial={{ width: 0 }}
      animate={{ width: reservedWidth }}
      exit={{ width: 0 }}
      transition={{ type: 'tween', duration: shouldDisableTransitions ? 0 : 0.15 }}
      className="relative h-full shrink-0"
    >
      <motion.div
        aria-hidden="true"
        initial={{ left: -viewportWidth, right: 0, opacity: isDrawerMode ? 1 : 0 }}
        animate={{ left: overlayLeftInset, opacity: isDrawerMode ? 1 : 0, right: overlayRightInset }}
        exit={{ left: -viewportWidth, right: 0, opacity: 0 }}
        transition={{
          type: 'tween',
          left: { duration: shouldDisableTransitions ? 0 : 0.15 },
          opacity: { duration: overlayOpacityDuration },
          right: { duration: shouldDisableTransitions ? 0 : 0.15 },
        }}
        className={cn(
          'absolute inset-y-0 z-10 bg-black/7 transition-colors duration-150',
          isDrawerMode ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none',
          !isResizing && 'hover:bg-black/3',
        )}
        onClick={isDrawerMode ? handleOverlayClick : undefined}
      />

      <motion.div
        initial={{ width: 0 }}
        animate={{ width: widthPx }}
        exit={{ width: 0 }}
        transition={{ type: 'tween', duration: shouldDisableTransitions ? 0 : 0.2 }}
        className="absolute top-0 right-0 z-30 h-full"
      >
        {/* Resize handle */}
        <div onMouseDown={handleMouseDown} className="absolute top-0 -left-2.5 z-40 h-full w-5 cursor-ew-resize">
          <div
            className={cn(
              'absolute top-1/2 left-1/2 z-20 flex h-7 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-0.75 rounded-md border border-gray-300 duration-150 hover:bg-gray-100',
              isResizing ? 'bg-gray-100' : 'bg-white',
            )}
          >
            <Icon className="lucide--chevrons-left-right text-xs text-gray-500" />
          </div>
        </div>

        <div
          className={cn(
            'flex h-full flex-col overflow-hidden border-l bg-white',
            isResizing ? 'border-teal-400' : 'border-gray-200',
            isDrawerMode ? 'shadow-2xl' : 'shadow-none',
            'transition-[border-color,box-shadow] duration-300',
          )}
        >
          {/* Reference list bar */}
          <div className="border-b p-2.5 py-2 pr-0">
            <div className="flex items-center justify-between gap-2">
              {references.length > 0 && (
                <div className="flex w-full gap-1 overflow-x-auto mask-r-from-95% pr-5 [scrollbar-width:none]">
                  {references.map((ref) => (
                    <button
                      key={ref.id}
                      onClick={() => onSelectReference(ref.id, ref.referenceType)}
                      className={cn(
                        'shrink-0 rounded-md border px-2 py-0.5 text-xs ring-offset-1 transition-colors duration-150 focus-visible:ring-1 focus-visible:outline-none',
                        REFERENCE_TYPE_STYLES[ref.referenceType].chip,
                        ref.id === activeReferenceId ? '' : 'opacity-50 hover:opacity-80',
                      )}
                    >
                      {ref.index}
                    </button>
                  ))}
                </div>
              )}

              <Button onClick={onClose} variant={'ghost'} size={'iconSm'} className="mr-2">
                <Icon className="lucide--panel-right-close" />
              </Button>
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-hidden">
            {activeReference?.referenceType === ReferenceType.Chunk && (
              <ChunkDocumentDrawerContent chunkId={activeReferenceId} />
            )}
            {activeReference?.referenceType === ReferenceType.Document && (
              <DocumentDrawerContent docId={activeReferenceId} />
            )}
            {activeReference?.referenceType === ReferenceType.Fact && (
              <FactDocumentDrawerContent factId={activeReferenceId} />
            )}
            {activeReference?.referenceType === ReferenceType.ThreadFileChunk && (
              <ThreadFileChunkDrawerContent chunkId={activeReferenceId} />
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
